import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { adminApi } from "@/lib/lnlApi";
import {
    FONT_CATALOG, COLOR_TOKENS, THEME_PRESETS,
    applyTheme, clearAppliedTheme, loadGoogleFont, contrastRatio, contrastLabel,
} from "@/lib/theme";
import { SettingsContext } from "@/components/layout/SettingsContext";
import { Save, RotateCcw, Eye, Palette, Type as TypeIcon, LayoutGrid, ArrowLeft } from "lucide-react";

const DEFAULT_KEY = "lnl_default";

function cloneTheme(t) { return JSON.parse(JSON.stringify(t || THEME_PRESETS[DEFAULT_KEY])); }

// Merge partial theme into full theme (fills missing colors/fonts from LNL default)
function fillTheme(partial) {
    const base = cloneTheme(THEME_PRESETS[DEFAULT_KEY]);
    if (!partial) return base;
    return {
        preset: partial.preset || DEFAULT_KEY,
        fonts: { ...base.fonts, ...(partial.fonts || {}) },
        colors: { ...base.colors, ...(partial.colors || {}) },
    };
}

export default function ThemeStudio() {
    const settings = useContext(SettingsContext);
    const [current, setCurrent] = useState(null);   // theme actually saved
    const [draft, setDraft] = useState(null);       // theme being edited
    const [tab, setTab] = useState("presets");
    const [preview, setPreview] = useState(false);
    const [busy, setBusy] = useState(false);
    const originalRef = useRef(null);               // pre-live-preview snapshot

    // Initial load: read from settings context (available immediately if PublicLayout provided it)
    useEffect(() => {
        (async () => {
            const { data } = await adminApi.get("/cms/settings");
            const t = fillTheme(data?.theme);
            setCurrent(t); setDraft(t);
            originalRef.current = t;
        })();
    }, []);

    // Turn live preview on/off — apply/revert to DOM
    useEffect(() => {
        if (preview && draft) applyTheme(draft);
        else if (!preview && current) applyTheme(current); // revert to saved
        // On unmount, restore saved theme so we don't leak preview to other pages
        return () => { if (current) applyTheme(current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preview, draft?.preset, JSON.stringify(draft?.fonts), JSON.stringify(draft?.colors)]);

    if (!draft) return <div className="text-lnl-gray">Loading Theme Studio…</div>;

    function applyPreset(key) {
        const preset = THEME_PRESETS[key];
        if (!preset) return;
        setDraft({ preset: key, fonts: { ...preset.fonts }, colors: { ...preset.colors } });
    }
    function setColor(tokenKey, hex) {
        setDraft({ ...draft, preset: "custom", colors: { ...draft.colors, [tokenKey]: hex } });
    }
    function setFont(role, family) {
        loadGoogleFont(family);
        setDraft({ ...draft, preset: "custom", fonts: { ...draft.fonts, [role]: family } });
    }

    async function onSave() {
        setBusy(true);
        try {
            await adminApi.patch("/cms/settings", { theme: draft });
            setCurrent(draft);
            originalRef.current = draft;
            applyTheme(draft);
            toast.success("Theme saved");
        } catch (e) {
            toast.error("Could not save theme: " + (e?.response?.data?.detail || e.message));
        } finally { setBusy(false); }
    }
    function onCancel() {
        setDraft(current);
        setPreview(false);
        applyTheme(current);
        toast.success("Reverted to last saved theme");
    }
    function onResetDefault() {
        if (!window.confirm("Reset to the LNL Default preset? Your unsaved edits will be lost.")) return;
        const t = cloneTheme(THEME_PRESETS[DEFAULT_KEY]);
        setDraft(t);
        toast.success("Restored LNL Default (not yet saved)");
    }

    const dirty = JSON.stringify(current) !== JSON.stringify(draft);

    return (
        <div data-testid="theme-studio">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div>
                    <div className="lnl-eyebrow">Website customization</div>
                    <h2 className="font-heading text-xl font-bold text-lnl-charcoal">Color & Font Studio</h2>
                    <div className="text-xs text-lnl-gray">Preset: <b>{THEME_PRESETS[draft.preset]?.label || "Custom"}</b>{dirty && <span className="ml-2 text-amber-800">· Unsaved changes</span>}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <label className="inline-flex items-center gap-2 text-sm cursor-pointer bg-white border border-lnl-border rounded-full px-3 py-1.5" data-testid="theme-preview-toggle">
                        <input type="checkbox" checked={preview} onChange={(e) => setPreview(e.target.checked)} />
                        <Eye className="h-4 w-4 text-lnl-green-deep" /> Live preview
                    </label>
                    <button onClick={onCancel} disabled={!dirty} className="lnl-btn-secondary text-sm py-2" data-testid="theme-cancel"><ArrowLeft className="h-4 w-4" /> Cancel</button>
                    <button onClick={onResetDefault} className="lnl-btn-secondary text-sm py-2" data-testid="theme-reset"><RotateCcw className="h-4 w-4" /> Reset to LNL Default</button>
                    <button onClick={onSave} disabled={busy || !dirty} className="lnl-btn-primary text-sm py-2" data-testid="theme-save"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save theme"}</button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex flex-wrap gap-1 border-b border-lnl-border mb-4" role="tablist">
                {[
                    { k: "presets", label: "Presets", Icon: LayoutGrid },
                    { k: "fonts", label: "Fonts", Icon: TypeIcon },
                    { k: "colors", label: "Colors", Icon: Palette },
                ].map(({ k, label, Icon }) => (
                    <button key={k} onClick={() => setTab(k)} role="tab" aria-selected={tab === k}
                        data-testid={`theme-tab-${k}`}
                        className={`inline-flex items-center gap-2 text-sm font-heading font-semibold px-3 py-2 border-b-2 -mb-px ${tab === k ? "border-lnl-green text-lnl-green-deep" : "border-transparent text-lnl-gray hover:text-lnl-charcoal"}`}>
                        <Icon className="h-4 w-4" /> {label}
                    </button>
                ))}
            </div>

            {tab === "presets" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="theme-presets">
                    {Object.entries(THEME_PRESETS).map(([key, p]) => {
                        const isDraft = draft.preset === key;
                        const isSaved = current?.preset === key;
                        return (
                            <button key={key} onClick={() => applyPreset(key)}
                                data-testid={`preset-${key}`}
                                className={`text-left lnl-card p-4 transition-all ${isDraft ? "ring-2 ring-lnl-green" : ""}`}>
                                <div className="flex items-center justify-between gap-2">
                                    <div className="font-heading font-bold text-lnl-charcoal">{p.label}</div>
                                    <div className="flex items-center gap-1.5">
                                        {isSaved && <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200 font-heading font-semibold uppercase tracking-wider" title="Currently live on the website">Live</span>}
                                        {isDraft && <span className="text-[10px] px-2 py-0.5 rounded bg-lnl-green-bg text-lnl-green-deep border border-lnl-green/20 font-heading font-semibold uppercase tracking-wider">Active</span>}
                                    </div>
                                </div>
                                <div className="mt-3 flex gap-1.5">
                                    {["primary","primary_deep","gold","ink","bg","charcoal"].map((k) => (
                                        <span key={k} className="w-6 h-6 rounded-md border border-lnl-border" style={{ background: p.colors[k] }} title={k} />
                                    ))}
                                </div>
                                <div className="mt-3 text-[11px] text-lnl-gray">
                                    Heading: <b style={{ fontFamily: `"${p.fonts.heading}"` }}>{p.fonts.heading}</b> · Body: <b style={{ fontFamily: `"${p.fonts.body}"` }}>{p.fonts.body}</b>
                                </div>
                            </button>
                        );
                    })}
                </div>
            )}

            {tab === "fonts" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="theme-fonts">
                    {[
                        { role: "heading", label: "Heading font" },
                        { role: "body", label: "Body font" },
                    ].map(({ role, label }) => (
                        <div key={role} className="lnl-card p-4">
                            <label className="text-xs text-lnl-gray font-heading font-semibold">{label}</label>
                            <select value={draft.fonts[role]} onChange={(e) => setFont(role, e.target.value)}
                                className="mt-1 w-full rounded-md border border-lnl-border px-3 py-2 bg-white"
                                data-testid={`font-${role}`}>
                                {FONT_CATALOG.map((f) => <option key={f.key} value={f.key}>{f.key}</option>)}
                            </select>
                            <div className="mt-3 p-3 rounded-md border border-lnl-border bg-white">
                                <div style={{ fontFamily: `"${draft.fonts[role]}"` }} className={role === "heading" ? "text-3xl font-bold" : "text-base leading-relaxed"}>
                                    {role === "heading"
                                        ? "Build Your Future at Lyceum Northern Luzon"
                                        : "Career-focused programs, complete basic education and a supportive academic community in Urdaneta City."}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {tab === "colors" && (
                <div className="space-y-4" data-testid="theme-colors">
                    {["Brand", "Surface", "Text"].map((group) => (
                        <div key={group} className="lnl-card p-4">
                            <div className="lnl-eyebrow mb-3">{group}</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                {COLOR_TOKENS.filter((t) => t.group === group).map((t) => (
                                    <div key={t.key} className="flex items-center gap-3">
                                        <input type="color" value={draft.colors[t.key] || "#000000"} onChange={(e) => setColor(t.key, e.target.value)} className="w-12 h-10 rounded-md border border-lnl-border cursor-pointer" data-testid={`color-${t.key}`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-xs font-heading font-semibold text-lnl-charcoal">{t.label}</div>
                                            <input type="text" value={draft.colors[t.key] || ""} onChange={(e) => setColor(t.key, e.target.value)} className="w-full rounded-md border border-lnl-border px-2 py-1 text-xs font-mono" data-testid={`color-hex-${t.key}`} />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}

                    {/* Contrast warnings */}
                    <div className="lnl-card p-4" data-testid="contrast-panel">
                        <div className="lnl-eyebrow mb-3">Contrast checks</div>
                        <div className="space-y-2">
                            {[
                                { name: "Primary button text on Primary background", fg: "#ffffff", bg: draft.colors.primary },
                                { name: "Body text on page background", fg: draft.colors.charcoal, bg: draft.colors.bg },
                                { name: "Muted text on off-white surface", fg: draft.colors.gray, bg: draft.colors.off_white },
                                { name: "Ink on gold accent", fg: draft.colors.ink, bg: draft.colors.gold },
                            ].map((p) => {
                                const ratio = contrastRatio(p.fg, p.bg);
                                const info = contrastLabel(ratio);
                                const tone = info.tone === "ok" ? "text-emerald-800 bg-emerald-50 border-emerald-200"
                                          : info.tone === "warn" ? "text-amber-800 bg-amber-50 border-amber-200"
                                          : "text-rose-800 bg-rose-50 border-rose-200";
                                return (
                                    <div key={p.name} className="flex items-center gap-3 text-sm">
                                        <span className="w-8 h-8 rounded-md border border-lnl-border flex items-center justify-center text-xs font-bold" style={{ background: p.bg, color: p.fg }}>Aa</span>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-lnl-charcoal">{p.name}</div>
                                            <div className="text-[11px] text-lnl-gray">Ratio: <b>{ratio.toFixed(2)}</b></div>
                                        </div>
                                        <span className={`text-[10px] px-2 py-1 rounded border font-heading font-semibold uppercase tracking-wider ${tone}`}>{info.level} · {info.msg}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            )}

            {/* Preview surface — always visible in-page (uses inline styles so it doesn't depend on live-preview) */}
            <div className="mt-6 lnl-card p-6" data-testid="theme-preview">
                <div className="lnl-eyebrow mb-3">Preview</div>
                <div style={{ background: draft.colors.off_white, borderRadius: 8, padding: 16, border: `1px solid ${draft.colors.border}` }}>
                    <div style={{ fontFamily: `"${draft.fonts.heading}"`, fontWeight: 800, color: draft.colors.charcoal, fontSize: 28, letterSpacing: "-0.02em" }}>
                        Build Your Future at Lyceum Northern Luzon
                    </div>
                    <div style={{ fontFamily: `"${draft.fonts.body}"`, color: draft.colors.gray, fontSize: 14, marginTop: 8, lineHeight: 1.6 }}>
                        Career-focused programs, complete basic education and a supportive academic community in Urdaneta City, Pangasinan.
                    </div>
                    <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ background: draft.colors.gold, color: draft.colors.ink, padding: "8px 18px", borderRadius: 999, fontFamily: `"${draft.fonts.heading}"`, fontSize: 13, fontWeight: 700 }}>Start Your Application</span>
                        <span style={{ background: "transparent", color: "#fff", padding: "8px 18px", borderRadius: 999, fontFamily: `"${draft.fonts.heading}"`, fontSize: 13, fontWeight: 700, border: "1px solid rgba(255,255,255,0.6)", backgroundColor: draft.colors.primary_deep }}>Explore Programs</span>
                        <span style={{ background: draft.colors.primary, color: "#fff", padding: "8px 18px", borderRadius: 999, fontFamily: `"${draft.fonts.heading}"`, fontSize: 13, fontWeight: 700 }}>Primary CTA</span>
                    </div>
                </div>
                <div className="mt-3 text-[11px] text-lnl-gray">Turn on <b>Live preview</b> at the top to apply the draft theme across the whole admin. Save to persist site-wide.</div>
            </div>
        </div>
    );
}
